import { HTMLStyledProps, forwardRef, styled } from "@design-system/core"
import * as React from "react"
import { useFormControlContext } from "./form-control"

export interface FormLabelProps extends HTMLStyledProps<"label"> {
  /**
   * @type React.ReactElement
   */
  requiredIndicator?: React.ReactElement
}

/**
 * Used to enhance the usability of form controls.
 *
 * It is used to inform users as to what information
 * is requested for a form field.
 *
 * ♿️ Accessibility: Every form field should have a form label.
 */
export const FormLabel = forwardRef<FormLabelProps, "label">((props, ref) => {
  const {
    children,
    requiredIndicator = <RequiredIndicator />,
    style,
    ...rest
  } = props

  const field = useFormControlContext()
  const ownProps = field?.getLabelProps(rest, ref) ?? { ref, ...rest }

  return (
    <styled.label {...ownProps} style={style}>
      {children}
      {field?.isRequired ? requiredIndicator : null}
    </styled.label>
  )
})

export interface RequiredIndicatorProps extends HTMLStyledProps<"span"> {}

/**
 * Used to show a "required" text or an asterisks (*) to indicate that
 * a field is required.
 */
export const RequiredIndicator = forwardRef<RequiredIndicatorProps, "span">(
  (props, ref) => {
    const { style, ...rest } = props
    const field = useFormControlContext()

    if (!field?.isRequired) return null

    return (
      <styled.span
        {...field?.getRequiredIndicatorProps(rest, ref)}
        style={style}
      />
    )
  },
)
